import React, { useContext, useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import Badge from './Badge';
import { computeActionBarLayout } from './actionBarLadder';
import './ActionBar.css';

/**
 * Kompakt-Zustand der Aktions-Buttons, gefüllt von der Leiste selbst.
 * Default `false`, damit `Action` auch außerhalb einer ActionBar funktioniert
 * (Showcase, Tests).
 */
export const ActionBarContext = React.createContext({ actionsCompact: false });

const CTX_FULL = { actionsCompact: false };
const CTX_ICON = { actionsCompact: true };

/** Fragmente auflösen, damit `left`/`right` als <>…</> übergeben werden können. */
function flattenSlot(node) {
  const out = [];
  React.Children.forEach(node, (child) => {
    if (child === null || child === undefined || child === false) return;
    if (React.isValidElement(child) && child.type === React.Fragment) {
      out.push(...flattenSlot(child.props.children));
      return;
    }
    out.push(child);
  });
  return out;
}

const isType = (el, type) => React.isValidElement(el) && el.type === type;

/**
 * Baustein 1 — Action-Bar.
 * Single-Line-Bereich am oberen Seitenrand, ersetzt den klassischen Page-Header.
 *
 * Die Leiste bleibt auf JEDER Breite einzeilig: sie misst ihre Elemente selbst
 * und baut in fester Rangfolge ab (Spec
 * docs/superpowers/specs/2026-07-27-actionbar-mobile-design.md):
 * (1) alle Button-Beschriftungen gemeinsam zu Icons, (2) Werte einzeln von
 * rechts nach links, (3) der Titel kürzt per Ellipsis.
 *
 * Props:
 *   left      — `Marker`, `Title`, `Stat` und `Action` in beliebiger Reihenfolge;
 *               die Reihenfolge der `Stat`s ist ihre Wichtigkeit (rechts weicht zuerst)
 *   right     — Aktionen (Primary ganz rechts), optional ein Zahnrad davor
 *   divider   — boolean, Border-Bottom (default false). Bei Seiten OHNE
 *               FilterStrip drunter auf `true` setzen, damit die Trennung zum
 *               Content visuell klar bleibt; mit FilterStrip trennt dessen
 *               eigene Surface-Box schon.
 *
 * Trenner zwischen Werten setzt die Leiste selbst — nur zwischen den gerade
 * sichtbaren. Es gibt deshalb kein Trenner-Element für Konsumenten: ein manuell
 * platzierter Trenner überlebt das Ausblenden seines Nachbarn nicht und
 * hinterlässt einen verwaisten Punkt am Zeilenanfang.
 *
 * Die Mess-Schicht klont die **rechte** Gruppe als Ganzes in beiden Zuständen —
 * so wird auch ein von der Seite mitgebrachter Knoten erfasst, der den
 * Kompakt-Zustand selbst aus dem Context liest.
 * Die **linke** Gruppe wird NICHT geklont: dort liegen von der Seite übergebene
 * Knoten mit `ref` — ein zweites Exemplar des Rename-Eingabefelds des
 * DeckEditors würde dessen `ref` belegen und den Fokus ins Unsichtbare schicken.
 * Ihre Breiten kommen deshalb aus der echten Zeile (Titel per `scrollWidth`),
 * nur die eigenen `Action`-Bausteine links werden einzeln geklont.
 */
export default function ActionBar({ left, right, divider = false }) {
  const rowRef = useRef(null);
  const leftRef = useRef(null);
  const measureRef = useRef(null);
  const prevHiddenRef = useRef([]);
  const [layout, setLayout] = useState({ actionsCompact: false, hiddenStatIds: [] });

  const leftKids = flattenSlot(left);
  const marker = leftKids.find((k) => isType(k, ActionBar.Marker)) ?? null;
  const title = leftKids.find((k) => isType(k, ActionBar.Title)) ?? null;
  const statKids = leftKids.filter((k) => isType(k, ActionBar.Stat));
  const leftActions = leftKids.filter((k) => isType(k, ActionBar.Action));
  const otherLeft = leftKids.filter(
    (k) => k !== marker && k !== title && !statKids.includes(k) && !leftActions.includes(k)
  );
  const stats = statKids.map((el, i) => ({ el, id: el.props.id ?? `stat-${i}` }));

  // ── Messen + Rechnen (vor dem ersten Paint — kein Aufblitzen) ──
  useLayoutEffect(() => {
    const rowEl = rowRef.current;
    const leftEl = leftRef.current;
    const measureEl = measureRef.current;
    if (!rowEl || !leftEl || !measureEl) return undefined;

    const recompute = () => {
      const rowStyle = getComputedStyle(rowEl);
      const outerGap = parseFloat(rowStyle.columnGap) || 0;
      const containerWidth =
        rowEl.clientWidth -
        (parseFloat(rowStyle.paddingLeft) || 0) -
        (parseFloat(rowStyle.paddingRight) || 0);
      // Nicht messbar (jsdom, display:none, noch 0 breit): Zustand nicht anfassen,
      // sonst würde eine 0-Breite alles ausblenden.
      if (containerWidth <= 0) return;

      const innerGap = parseFloat(getComputedStyle(leftEl).columnGap) || 0;

      // Linke Aktionen: eigene Bausteine, einzeln in beiden Zuständen geklont
      const widthOf = (attr, i) =>
        measureEl.querySelector(`[data-ab-${attr}="${i}"]`)?.offsetWidth || 0;
      let leftActionsFull = 0;
      let leftActionsIcon = 0;
      leftActions.forEach((_, i) => {
        leftActionsFull += widthOf('action-full', i);
        leftActionsIcon += widthOf('action-icon', i);
      });

      // Titel: die ungekürzte Breite steht als scrollWidth des Textbereichs
      // in der echten Zeile — deshalb muss er nicht geklont werden.
      const titleEl = leftEl.querySelector('[data-ab-role="title"]');
      let titleWidth = 0;
      if (titleEl) {
        const textEl = titleEl.querySelector('[data-ab-role="title-text"]');
        if (textEl) {
          const actionEl = titleEl.querySelector('[data-ab-role="title-action"]');
          const titleGap = parseFloat(getComputedStyle(titleEl).columnGap) || 0;
          titleWidth =
            textEl.scrollWidth + (actionEl ? actionEl.offsetWidth + titleGap : 0);
        } else {
          titleWidth = titleEl.offsetWidth; // Rename-Modus: kein kürzender Textbereich
        }
      }

      const fixedWidthOf = (sel) => leftEl.querySelector(sel)?.offsetWidth || 0;
      const markerWidth = fixedWidthOf('[data-ab-role="marker"]');
      const otherWidth = fixedWidthOf('[data-ab-role="other"]');

      const next = computeActionBarLayout({
        containerWidth,
        innerGap,
        outerGap,
        sepWidth: measureEl.querySelector('[data-ab-measure="sep"]')?.offsetWidth || 0,
        leftFixedFullWidth: markerWidth + titleWidth + otherWidth + leftActionsFull,
        leftFixedIconWidth: markerWidth + titleWidth + otherWidth + leftActionsIcon,
        leftFixedCount:
          (marker ? 1 : 0) + (title ? 1 : 0) + (otherLeft.length > 0 ? 1 : 0) + leftActions.length,
        stats: Array.from(measureEl.querySelectorAll('[data-ab-stat]')).map((el) => ({
          id: el.dataset.abStat,
          width: el.offsetWidth,
        })),
        rightFullWidth:
          measureEl.querySelector('[data-ab-measure="right-full"]')?.offsetWidth || 0,
        rightIconWidth:
          measureEl.querySelector('[data-ab-measure="right-icon"]')?.offsetWidth || 0,
        previousHiddenStatIds: prevHiddenRef.current,
      });
      prevHiddenRef.current = next.hiddenStatIds;
      setLayout((cur) =>
        cur.actionsCompact === next.actionsCompact &&
        cur.hiddenStatIds.join('|') === next.hiddenStatIds.join('|')
          ? cur
          : next
      );
    };

    recompute();
    if (typeof ResizeObserver === 'undefined') return undefined; // jsdom
    const ro = new ResizeObserver(recompute);
    ro.observe(rowEl);
    Array.from(measureEl.children).forEach((el) => ro.observe(el));
    return () => ro.disconnect();
  });
  // Ohne Dependency-Array: die Kinder (Zahlen, Labels) ändern sich mit jedem
  // Render, die Mess-Elemente werden dann neu observiert. `setLayout` hat oben
  // einen Bail-out, daher keine Endlos-Schleife. Analog ui/FilterStrip.

  const hidden = new Set(layout.hiddenStatIds);
  const visibleStats = stats.filter((s) => !hidden.has(s.id));
  const statRow = visibleStats.flatMap((s, i) => {
    const el = React.cloneElement(s.el, { key: `stat-${s.id}` });
    return i === 0
      ? [el]
      : [<span className="action-bar__sep" aria-hidden key={`sep-${s.id}`}>·</span>, el];
  });

  const ctxValue = layout.actionsCompact ? CTX_ICON : CTX_FULL;

  return (
    <div className={`action-bar${divider ? ' action-bar--divided' : ''}`} ref={rowRef}>
      <ActionBarContext.Provider value={ctxValue}>
        <div className="action-bar__left" ref={leftRef}>
          {leftActions.map((el, i) => React.cloneElement(el, { key: `la-${i}` }))}
          {otherLeft.length > 0 && (
            <span className="action-bar__other" data-ab-role="other">
              {otherLeft.map((el, i) =>
                React.isValidElement(el) ? React.cloneElement(el, { key: `o-${i}` }) : el
              )}
            </span>
          )}
          {marker}
          {title}
          {statRow}
        </div>
        <div className="action-bar__right">{right}</div>
      </ActionBarContext.Provider>

      <div className="action-bar__measure" ref={measureRef} aria-hidden="true">
        {stats.map((s) => (
          <span key={`m-${s.id}`} className="action-bar__measure-stat" data-ab-stat={s.id}>
            {s.el}
          </span>
        ))}
        <span className="action-bar__sep" data-ab-measure="sep">·</span>
        {leftActions.map((el, i) => (
          <React.Fragment key={`a-${i}`}>
            <span data-ab-action-full={i}>
              <ActionBarContext.Provider value={CTX_FULL}>{el}</ActionBarContext.Provider>
            </span>
            <span data-ab-action-icon={i}>
              <ActionBarContext.Provider value={CTX_ICON}>{el}</ActionBarContext.Provider>
            </span>
          </React.Fragment>
        ))}
        {/* Ohne `action-bar__right`-Klasse — sonst treffen Selektoren auf die
            Kopien mit; das Layout kommt in ActionBar.css über data-ab-measure. */}
        <span data-ab-measure="right-full">
          <ActionBarContext.Provider value={CTX_FULL}>{right}</ActionBarContext.Provider>
        </span>
        <span data-ab-measure="right-icon">
          <ActionBarContext.Provider value={CTX_ICON}>{right}</ActionBarContext.Provider>
        </span>
      </div>
    </div>
  );
}

/**
 * Stat — Zahl + Label, als kohäsives Flex-Item. Der Wrapper sorgt dafür, dass
 * der Inline-Whitespace zwischen Zahl und Label nicht vom Flex-Gap der Leiste
 * verschluckt wird.
 *
 * Die Position ist die Wichtigkeit: ausgeblendet wird von rechts nach links.
 * `id` hält die Hysterese stabil, wenn Werte bedingt gerendert werden.
 * Geldwerte tragen kein Label, und jedes Label benennt einen Zustand oder Ort
 * („im Export", „fehlend"), damit ein Wert ohne seine Nachbarn verständlich ist.
 *
 * Beispiel: <ActionBar.Stat id="fehlend" value="6 / 85" label="fehlend" />
 * Geldwert: <ActionBar.Stat id="wert">19.12 €</ActionBar.Stat>
 */
ActionBar.Stat = function ActionBarStat({ value, label, children }) {
  return (
    <span className="action-bar__stat">
      {children ?? (<><strong>{value}</strong> {label}</>)}
    </span>
  );
};

/**
 * Action — Aktions-Button der Leiste.
 * Icon und Label sind getrennte Props, weil die Icon-Stufe der Abbau-Rangfolge
 * den Text entfernen muss und CSS keinen Textknoten ausblenden kann.
 *
 * `menu` setzt ein Chevron hinter die Beschriftung: die Aktion öffnet ein Menü,
 * statt sofort zu handeln. In der Icon-Stufe fällt es mit dem Text weg — dort
 * öffnet das Antippen ohnehin das Menü.
 *
 * Restliche Props und `ref` gehen an den Button, damit die Aktion der Trigger
 * eines `ui/ContextMenu` sein kann: Radix reicht Öffnen-Handler und
 * ARIA-Attribute per `asChild` an ihr Kind durch.
 *
 * Beispiel: <ActionBar.Action icon={Upload} label="Export" variant="primary" onClick={…} />
 * Als Menü: <ContextMenu trigger={<ActionBar.Action icon={Download} label="Importieren" menu />}>…
 */
ActionBar.Action = React.forwardRef(function ActionBarAction({
  icon: Icon,
  label,
  variant = 'secondary',
  onClick,
  disabled = false,
  title,
  menu = false,
  ...rest
}, ref) {
  const { actionsCompact } = useContext(ActionBarContext);
  const classes = ['action-bar__action', `btn-${variant}`];
  if (actionsCompact) classes.push('action-bar__action--compact');
  return (
    <button
      ref={ref}
      {...rest}
      type="button"
      className={classes.join(' ')}
      data-ab-role="action"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={title ?? label}
    >
      <Icon size={16} aria-hidden />
      {!actionsCompact && (
        <>
          <span className="action-bar__action-label">{label}</span>
          {menu && <ChevronDown size={14} aria-hidden />}
        </>
      )}
    </button>
  );
});
ActionBar.Action.displayName = 'ActionBarAction';

/**
 * Marker — Status-Kennzeichnung, steht vor Titel und Werten.
 * Desktop: beschriftete Pille. Mobile (≤768px): Symbol mit Tooltip — die
 * Wort-Pille braucht dort ~106px, das Symbol ~30px, und ein Schloss sagt
 * „schreibgeschützt" direkter als das Wort (Spec §3.3/§5). Beide Formen stehen
 * im DOM, CSS schaltet um; die Pillen-Optik kommt aus `ui/Badge`.
 *
 * Beispiel: <ActionBar.Marker icon={Lock} label="Physisch" title="…schreibgeschützt" />
 */
ActionBar.Marker = function ActionBarMarker({ icon: Icon, label, tone = 'pending', title }) {
  const tip = title ?? label;
  return (
    <span className="action-bar__marker" data-ab-role="marker">
      <span className="action-bar__marker-word" title={tip}>
        <Badge tone={tone} size="lg">{label}</Badge>
      </span>
      <span className="action-bar__marker-icon" title={tip} aria-label={label} role="img">
        <Badge tone={tone}><Icon size={14} aria-hidden /></Badge>
      </span>
    </span>
  );
};

/**
 * Title — Seiten-Titel in der Leiste (heute nur der Deckname im Editor).
 * Weicht nie aus und kürzt als Letztes — und zwar NUR im Textbereich: der
 * `action`-Slot (Stift) bleibt in jeder Stufe vollständig sichtbar, weil er
 * sonst als Erstes wegfällt und das Umbenennen auf schmalen Geräten
 * unerreichbar wird.
 *
 * `editing` rendert die Kinder ohne kürzenden Textbereich — für das
 * Inline-Rename-Eingabefeld, das nicht per Ellipsis beschnitten werden darf.
 *
 * Beispiel: <ActionBar.Title action={<button …><Pencil /></button>}>Deckname</ActionBar.Title>
 */
ActionBar.Title = function ActionBarTitle({ children, action, editing = false }) {
  return (
    <span className="action-bar__title" data-ab-role="title">
      {editing ? children : (
        <span className="action-bar__title-text" data-ab-role="title-text">{children}</span>
      )}
      {action && (
        <span className="action-bar__title-action" data-ab-role="title-action">{action}</span>
      )}
    </span>
  );
};
